/**
 * Deploy Command
 *
 * Deploy secrets to remote servers via rsync/SSH.
 */

import { spawn } from 'child_process';
import * as fs from 'fs-extra';
import * as path from 'path';
import { CredentialService } from '../core/service';
import { getEnvPath } from '../config/paths';
import * as output from '../utils/output';
import { confirm } from '../utils/prompts';

interface DeployOptions {
  remote: string;
  file?: string;
  restart?: string;
  yes?: boolean;
}

/**
 * Allowed restart command patterns (whitelist for security)
 * Only these patterns are allowed to prevent command injection
 */
const ALLOWED_RESTART_PATTERNS = [
  /^pm2 restart [\w\-]+$/,                    // pm2 restart app-name
  /^pm2 reload [\w\-]+$/,                     // pm2 reload app-name
  /^systemctl restart [\w\-\.]+$/,            // systemctl restart service
  /^systemctl reload [\w\-\.]+$/,             // systemctl reload service
  /^docker restart [\w\-]+$/,                 // docker restart container
  /^docker-compose restart$/,                 // docker-compose restart
  /^docker-compose up -d$/,                   // docker-compose up -d
  /^supervisorctl restart [\w\-:]+$/,         // supervisorctl restart process
  /^service [\w\-]+ restart$/,                // service name restart
];

/**
 * Validate restart command against whitelist
 */
function isValidRestartCommand(cmd: string): boolean {
  return ALLOWED_RESTART_PATTERNS.some(pattern => pattern.test(cmd.trim()));
}

/**
 * Parse remote string into components
 * Format: user@host:path or host:path
 */
function parseRemote(remote: string): { user?: string; host: string; path: string } | null {
  // Match user@host:path or host:path
  const match = remote.match(/^(?:([^@]+)@)?([^:]+):(.+)$/);
  if (!match) {
    return null;
  }
  return {
    user: match[1],
    host: match[2],
    path: match[3],
  };
}

/**
 * Run a command and return its output
 */
function runCommand(
  cmd: string,
  args: string[],
  options?: { cwd?: string }
): Promise<{ code: number; stdout: string; stderr: string }> {
  return new Promise((resolve) => {
    const proc = spawn(cmd, args, {
      cwd: options?.cwd,
      stdio: ['pipe', 'pipe', 'pipe'],
    });

    let stdout = '';
    let stderr = '';

    proc.stdout?.on('data', (data) => { stdout += data; });
    proc.stderr?.on('data', (data) => { stderr += data; });

    proc.on('close', (code) => {
      resolve({ code: code || 0, stdout, stderr });
    });

    proc.on('error', (err) => {
      resolve({ code: 1, stdout: '', stderr: err.message });
    });
  });
}

/**
 * Check if rsync is available
 */
async function hasRsync(): Promise<boolean> {
  const result = await runCommand('which', ['rsync']);
  return result.code === 0;
}

export async function deployCommand(project: string, options: DeployOptions): Promise<void> {
  const service = new CredentialService();

  // Check vault exists
  const vaults = await service.hasVault();
  if (!vaults.local && !vaults.global) {
    output.error('No vault found. Run `secretsage init` first.');
    process.exit(1);
  }

  // Validate remote format
  if (!options.remote) {
    output.error('--remote is required. Format: user@host:/path/to/destination');
    process.exit(1);
  }

  const remote = parseRemote(options.remote);
  if (!remote) {
    output.error('Invalid remote format. Use: user@host:/path or host:/path');
    process.exit(1);
  }

  // Check rsync availability
  if (!await hasRsync()) {
    output.error('rsync is required but not found in PATH.');
    output.info('Install rsync: apt install rsync (Ubuntu) or brew install rsync (macOS)');
    process.exit(1);
  }

  await service.init();

  // Get local env file path
  const localEnvPath = options.file || getEnvPath();

  // Show deployment plan
  output.header(`Deploy: ${project}`);
  output.divider();
  output.keyValue('Local file', localEnvPath);
  output.keyValue('Remote', options.remote);
  if (options.restart) {
    output.keyValue('Post-deploy', options.restart);
  }
  output.divider();

  // Check if env file exists, if not, grant all first
  if (!await fs.pathExists(localEnvPath)) {
    output.info('No .env file found. Granting all credentials...');
    const list = await service.list();
    if (list.length === 0) {
      output.error('No credentials in vault to deploy.');
      process.exit(1);
    }
    const names = list.map((c) => c.name);
    await service.grant(names, { backup: true });
    output.success(`Granted ${names.length} credentials to .env`);
  }

  // Show what will be deployed
  const envContent = await fs.readFile(localEnvPath, 'utf8');
  const envKeys = envContent
    .split('\n')
    .filter((line) => line.includes('=') && !line.startsWith('#'))
    .map((line) => line.split('=')[0]);

  output.info(`Will deploy ${envKeys.length} environment variables:`);
  envKeys.slice(0, 5).forEach((key) => {
    console.log(`  ${output.colors.key(key)}`);
  });
  if (envKeys.length > 5) {
    console.log(output.colors.muted(`  ... and ${envKeys.length - 5} more`));
  }
  console.log('');

  // Confirm deployment
  if (!options.yes) {
    const confirmed = await confirm(`Deploy to ${remote.host}?`);
    if (!confirmed) {
      output.info('Deployment cancelled.');
      return;
    }
  }

  // Build rsync command
  const remoteTarget = remote.user
    ? `${remote.user}@${remote.host}:${remote.path}`
    : `${remote.host}:${remote.path}`;

  const spin = output.spinner('Deploying...');
  spin.start();

  // Run rsync
  const rsyncResult = await runCommand('rsync', [
    '-avz',
    '--chmod=600',
    localEnvPath,
    remoteTarget,
  ]);

  if (rsyncResult.code !== 0) {
    spin.fail('Deployment failed');
    output.error(rsyncResult.stderr || 'rsync failed');
    process.exit(1);
  }

  spin.succeed('Files synced');

  // Run post-deploy command if specified
  if (options.restart) {
    // Validate restart command against whitelist to prevent command injection
    if (!isValidRestartCommand(options.restart)) {
      output.error('Invalid restart command. Allowed patterns:');
      output.info('  pm2 restart <app>');
      output.info('  pm2 reload <app>');
      output.info('  systemctl restart <service>');
      output.info('  docker restart <container>');
      output.info('  docker-compose restart');
      output.info('  supervisorctl restart <process>');
      output.info('  service <name> restart');
      process.exit(1);
    }

    const restartSpin = output.spinner(`Running: ${options.restart}`);
    restartSpin.start();

    const sshTarget = remote.user ? `${remote.user}@${remote.host}` : remote.host;
    const sshResult = await runCommand('ssh', [sshTarget, options.restart]);

    if (sshResult.code !== 0) {
      restartSpin.fail('Post-deploy command failed');
      output.warning(sshResult.stderr || 'Command failed');
    } else {
      restartSpin.succeed('Post-deploy command completed');
      if (sshResult.stdout.trim()) {
        console.log(output.colors.muted(sshResult.stdout.trim()));
      }
    }
  }

  console.log('');
  output.success(`Deployed to ${remote.host}:${remote.path}`);
}
